<template>
  <div
    class="t-border t-border-gray-200 t-shadow-lg t-py-2 t-px-3 t-bg-white md:t-rounded-lg"
  >
    <!-- Card Heading -->
    <div class="t-flex t-justify-between t-items-baseline t-py-2">
      <div class="t-flex t-space-x-2 t-items-center">
        <p
          class="t-text-2xl t-font-medium t-text-blue-500 hover:t-text-blue-900"
        >
          <a :href="'/send-freight/bookings/' + booking.id">
            {{ booking.number }}
          </a>
        </p>
        <span
          v-if="bookingStatus === 'BOOKED'"
          class="t-py t-px-2 t-rounded t-bg-orange t-text-white t-font-semibold"
        >
          BOOKED
        </span>
        <span
          v-if="bookingStatus === 'IN TRANSIT'"
          class="t-py t-px-2 t-rounded t-bg-dodger-blue t-text-white t-font-semibold"
        >
          IN TRANSIT
        </span>
        <span
          v-if="bookingStatus === 'CANCELLED'"
          class="t-py t-px-2 t-rounded t-bg-red-500 t-text-white t-font-semibold"
        >
          CANCELLED
        </span>
        <span
          v-if="bookingStatus === 'DELIVERED'"
          class="t-py t-px-2 t-rounded t-bg-green-600 t-text-white t-font-semibold"
        >
          DELIVERED
        </span>
      </div>
      <p class="t-font-light">${{ booking.price }}</p>
    </div>

    <div
      class="t-border-t-2 t-border-b-2 t-border-gray-300 t-flex t-flex-col t-pb-2 md:t-flex-row md:t-justify-between md:t-pb-0"
    >
      <div
        class="t-grid t-grid-rows-3 t-grid-cols-1 t-text-sm t-py-1 md:t-grid-rows-1 md:t-grid-cols-3 md:t-divide-x-2 md:t-divide-gray-300"
      >
        <div class="t-px-2 t-py-1">
          <p class="t-text-gray-700 t-uppercase t-text-xs t-font-semibold">
            Booking Reference
          </p>
          <p>{{ booking.sender_ref }}</p>
        </div>

        <div class="t-px-2 t-py-1">
          <p class="t-text-gray-700 t-uppercase t-text-xs t-font-semibold">
            Placed
          </p>
          <p>{{ booking.placed_at }}</p>
        </div>

        <div class="t-px-2 t-py-1">
          <p class="t-text-gray-700 t-uppercase t-text-xs t-font-semibold">
            Pickup Carrier
          </p>

          <span v-if="booking.status_id >= 3">{{ booking.carrier }}</span>
          <span v-else>
            Pending&nbsp;
            <span
              v-tooltip:bottom="[
                '<h6>Why does it say pending?</h6> Because we\'re filling empty space on trucks around New Zealand it can\
                take a little while for us to confirm exactly who the carrier will be.\
                Your booking is 100% guaranteed at the price you booked at, as we have\
                a vast network of carriers around the country',
                true,
              ]"
            >
              <i class="far fa-info-circle"></i>
            </span>
          </span>
        </div>
      </div>

      <div
        class="t-flex t-flex-col t-space-y-3 t-space-x-2 t-text-md md:t-block md:t-space-y-2 md:t-text-sm md:t-py-2"
      >
        <a
          v-if="bookingStatus !== 'DELIVERED' && bookingStatus !== 'CANCELLED'"
          :href="'/send-freight/bookings/' + booking.id"
          class="t-inline-block t-text-center t-px-2 t-py-1 t-rounded t-font-medium t-text-white t-bg-green-600 t-ml-2 md:t-mb-2 hover:t-bg-green-700 hover:t-text-white"
        >
          Track
        </a>

        <a
          v-if="booking.carrier !== 'Aramex'"
          :href="'/send-freight/bookings/' + booking.id + '/all'"
          target="_blank"
          class="t-inline-block t-text-center t-px-2 t-py-1 t-rounded t-font-medium t-text-white t-bg-dodger-blue t-ml-2 md:t-mb-2 hover:t-bg-blue-500 hover:t-text-white"
        >
          All Files
        </a>

        <a
          :href="'/send-freight/bookings/' + booking.id + '/address-label'"
          target="_blank"
          class="t-inline-block t-text-center t-px-2 t-py-1 t-rounded t-font-medium t-text-white t-bg-blue-600 t-ml-2 md:t-mb-2 hover:t-bg-blue-700 hover:t-text-white"
          id="address-label"
        >
          Address Label
        </a>

        <a
          v-if="hasConsignmentLabel"
          :href="'/send-freight/bookings/' + booking.id + '/consignment'"
          target="_blank"
          class="t-inline-block t-text-center t-px-2 t-py-1 t-rounded t-font-medium t-text-white t-bg-blue-600 t-ml-2 md:t-mb-2 hover:t-bg-blue-700 hover:t-text-white"
        >
          Consignment
        </a>

        <a
          v-if="booking.is_dg > 0"
          :href="'/send-freight/bookings/' + booking.id + '/dgs'"
          class="t-inline-block t-text-center t-px-2 t-py-1 t-rounded t-font-medium t-text-white t-bg-blue-600 t-ml-2 md:t-mb-2 hover:t-bg-blue-700 hover:t-text-white"
        >
          DGs
        </a>

        <a
          v-if="booking.has_pod"
          :href="'/send-freight/bookings/' + booking.id + '/proof-of-delivery'"
          class="t-inline-block t-text-center t-px-2 t-py-1 t-rounded t-font-medium t-text-white t-bg-blue-600 t-ml-2 md:t-mb-2 hover:t-bg-blue-700 hover:t-text-white"
        >
          Proof of Delivery
        </a>

        <a
          v-if="booking.has_credit_note"
          :href="'/send-freight/bookings/' + booking.id + '/credit-note'"
          class="t-inline-block t-text-center t-px-2 t-py-1 t-rounded t-font-medium t-text-white t-bg-blue-600 t-ml-2 md:t-mb-2 hover:t-bg-blue-700 hover:t-text-white"
        >
          Credit Note
        </a>

        <a
          v-if="bookingStatus === 'DELIVERED'"
          :href="`/send-freight/bookings/${booking.id}/damage-claims`"
          class="t-inline-block t-text-center t-px-2 t-py-1 t-rounded t-font-medium t-text-white t-bg-yellow-500 t-ml-2 md:t-mb-2 hover:t-bg-yellow-600 hover:t-text-white"
        >
          Claims
        </a>

        <a
          v-if="
            booking.invoice_ready || (booking.carrier === 'Pending' && !remove)
          "
          :href="'/send-freight/bookings/' + booking.id + '/invoice'"
          class="t-inline-block t-text-center t-px-2 t-py-1 t-rounded t-font-medium t-text-white t-bg-indigo-600 t-ml-2 md:t-mb-2 hover:t-bg-indigo-700 hover:t-text-white"
        >
          Invoice
        </a>
        <a
          v-else
          v-tooltip.top.start="'No invoice yet'"
          class="t-inline-block t-text-center t-px-2 t-py-1 t-rounded t-font-medium t-text-white t-bg-indigo-600 t-bg-opacity-50 t-ml-2 md:t-mb-2 hover:t-bg-indigo-700 hover:t-text-white"
        >
          <span class="t-text-white">Invoice</span>
        </a>

        <!--<a
          v-if="booking.carrier === 'Pending' && !remove"
          :href="'/send-freight/bookings/' + booking.id + '/invoice'"
          class="t-inline-block t-text-center t-px-2 t-py-1 t-rounded t-font-medium t-text-white t-bg-indigo-600 t-ml-2 md:t-mb-2 hover:t-bg-indigo-700 hover:t-text-white"
        >
          Invoice
        </a>-->

        <button
          v-if="
            booking.status_id < 9 && booking.carrier === 'Pending' && !remove
          "
          @click="cancelBooking(booking)"
          :disabled="isCancellingBooking"
          class="t-inline-block t-text-center t-px-2 t-py-1 t-rounded t-font-medium t-text-white t-bg-red-600 t-ml-2 md:t-mb-2 hover:t-bg-red-700 hover:t-text-white"
        >
          {{ cancelBookingText }}
        </button>
      </div>
    </div>

    <!-- Card Body -->
    <div
      class="t-grid t-grid-rows-3 t-grid-cols-1 t-gap-y-3 t-mt-2 t-px-2 md:t-grid-rows-1 md:t-grid-cols-12"
    >
      <div class="md:t-col-span-3 t-text-sm">
        <p class="t-text-xs t-uppercase t-text-blue-700 t-font-semibold">
          From
        </p>
        <p class="t-font-semibold">{{ booking.pickupLocation.data.name }}</p>
        <p>
          {{ booking.pickupLocation.data.address.data.street_number }}
          {{ booking.pickupLocation.data.address.data.street_name }}
        </p>
        <p>{{ booking.pickupLocation.data.address.data.suburb }}</p>
        <p>{{ booking.pickupLocation.data.address.data.city }}</p>

        <p class="t-font-medium t-mt-2">
          Pickup After:
          <span class="t-font-normal">{{ booking.pickup_at }}</span>
        </p>
        <p v-if="booking.pickup_before" class="t-font-medium">
          Pickup Before:
          <span class="t-font-normal">{{ booking.pickup_before }}</span>
        </p>

        <div
          v-if="booking.status_id <= 3"
          class="t-flex t-items-center t-mt-1 t-space-x-2"
          @click="$emit('showConfirmModal')"
        >
          <input
            type="checkbox"
            :checked="pickedUpChecked"
            class="t-w-4 t-h-4"
          />
          <label class="t-text-sm t-text-black t-font-medium t-m-0">
            Picked up
          </label>
        </div>

        <a
          v-if="
            booking.latestTruckAllocationFreightEvent && !booking.pickupMovement
          "
          class="t-font-medium"
        >
          Allocated to Truck:
          <span
            v-if="
              this.booking.latestTruckAllocationFreightEvent.data.truck_plate
            "
            @click="showingTruckLocation = true"
            class="t-font-normal t-underline"
          >
            {{
              this.booking.latestTruckAllocationFreightEvent.data.truck_plate
            }}
          </span>
          <span v-else class="t-font-normal"> Unknown </span>
        </a>
        <p v-if="booking.pickupMovement" class="t-font-medium">
          Picked Up At:
          <span class="t-font-normal">{{
            booking.pickupMovement.data.time
          }}</span>
        </p>
      </div>
      <div class="md:t-col-span-3 t-text-sm">
        <p class="t-text-xs t-uppercase t-text-blue-700 t-font-semibold">To</p>
        <p class="t-font-semibold">{{ booking.deliveryLocation.data.name }}</p>
        <p>
          {{ booking.deliveryLocation.data.address.data.street_number }}
          {{ booking.deliveryLocation.data.address.data.street_name }}
        </p>
        <p>{{ booking.deliveryLocation.data.address.data.suburb }}</p>
        <p>{{ booking.deliveryLocation.data.address.data.city }}</p>

        <p class="t-font-medium t-mt-2">
          Deliver By:
          <span class="t-font-normal">{{ booking.deliver_at }} </span>
        </p>
      </div>
      <div class="md:t-col-span-6">
        <p
          class="t-text-xs t-uppercase t-text-blue-700 t-font-semibold t-border-b-2 t-border-gray-300 t-pb-2 t-mb-2"
        >
          Freight
        </p>

        <div
          class="t-grid t-grid-cols-5 t-grid-rows-1 t-gap-x-2"
          v-for="freight in booking.lines.data"
          :key="freight.id"
        >
          <p class="t-col-span-3">
            {{ freight.quantity }} x {{ freight.packageType.data.name }}
            {{ freight.goodsType.data.name }}
          </p>
          <p>{{ parseFloat(freight.weight) }} kg</p>
          <p>{{ parseFloat(freight.volume) }} m<sup>3</sup></p>
        </div>

        <div
          class="t-grid t-grid-cols-5 t-grid-rows-1 t-font-medium t-mt-1 t-border-t t-border-gray-300 t-gap-x-2"
        >
          <p class="t-col-span-3">Total</p>
          <p>{{ parseFloat(booking.weight) }} kg</p>
          <p>{{ parseFloat(booking.volume) }} m<sup>3</sup></p>
        </div>

        <div
          v-if="booking.dg_classes"
          class="t-mt-1 t-border-t t-border-gray-300 t-text-sm"
        >
          <p><strong>DG Classes:</strong> {{ booking.dg_classes }}</p>
        </div>
      </div>
    </div>
    <current-location-modal
      :booking="booking"
      :visible="showingTruckLocation"
      @close="showingTruckLocation = false"
    ></current-location-modal>
  </div>
</template>

<script>
import moment from "moment";
import CurrentLocationModal from "./CurrentLocationModal";

export default {
  components: { CurrentLocationModal },

  name: "BookingCard",

  description: "",

  props: {
    booking: {
      type: Object,
      required: true,
    },
    pickedUpChecked: {
      type: Boolean,
    },
    bookingStatus: {
      type: String,
      default: null,
    },
    user: {
      type: Object,
    },
  },

  data() {
    return {
      date: moment(this.booking.cancel_time_left * 60000),
      userCanCancelBooking: true,
      loggedInUserId: window.Freighthub.user.id,
      isCancellingBooking: false,
      remove: false,
      showingTruckLocation: false
    };
  },

  mounted() {
    setInterval(() => {
      if (this.date === 0 || this.date > 900000) {
        return (this.date = 0);
      }
      this.date = moment(this.date.subtract(1, "seconds"));
    }, 1000);
  },

  methods: {
    async cancelBooking(booking) {
      try {
        this.isCancellingBooking = true;

        if (this.userCanCancelBooking) {
          let res = await axios.get(booking.id + "/cancel");
          if (res.data === "Assigned") {
            this.$swal({
              title:
                "Booking has already been assigned, please contact Freighthub if you still wish to cancel this booking!",
              type: "error",
              confirmButtonText: "Okay",
            });
          } else {
            this.$swal({
              title: "Booking cancelled!",
              type: "success",
              confirmButtonText: "Okay",
            });
            this.remove = true;
          }
        } else {
          // User must request a cancellation
          this.isCancellingBooking = true;
          this.$prompt("Please enter a reason for cancellation", {
            confirmButtonText: "Submit",
            cancelButtonText: "Cancel",
            inputErrorMessage: "Invalid input",
          })
            .then(({ value }) => {
              this.$intercom.boot({
                userId: this.loggedInUserId,
                name: this.user.name,
                email: this.user.email,
              });
              this.saving = false;
              this.$intercom.showNewMessage(
                "Hi, I would like to cancel booking #" +
                  booking.number +
                  ". Reason: " +
                  value
              );
            })
            .catch((error) => {
              console.log(error);
              this.saving = false;
              this.$msgbox({
                  type: "info",
                  title: 'Message',
                  message: 'Input canceled ',
                  confirmButtonText: 'OK',
              });
            });
        }
      } catch (error) {
        console.error(error);
        Event.$emit("serverError");
      } finally {
        this.isCancellingBooking = false;
      }
    },
  },
  computed: {
    cancelBookingText() {
      if (this.date > 0 && this.date < 900000) {
        this.userCanCancelBooking = true;
        return `Cancel Booking (${this.date.format("mm:ss")} left)`;
      }
      this.userCanCancelBooking = false;
      return "Request Cancellation";
    },
    /**
     * Check if the consignment label has been generated for external carriers so that it can be displayed to the user
     */
    hasConsignmentLabel() {
      axios.get('/send-freight/bookings/' + this.booking.id + '/address-label')
      .then(() => {
        return true;
      })
      .catch(() => {
        return false;
      });
    }
  },
};
</script>

<style scoped>
a {
  text-decoration: none;
}
</style>
